import React, { Component } from "react";
import IconWithBackground from "containers/chat/IconWithBackground/IconWithBackground";
import "./AddUserForm.scss";
import Input from "components/Input/Input";
import RadioGroup from "components/RadioGroup/RadioGroup";
import FormInputWithHeader from "components/FormInputWithHeader/FormInputWithHeader";
import { Col, Row } from "react-bootstrap";
import Select from "react-select";
import { uepClient } from "uepClient";
import Button from "Button/Button";
import { IoPersonCircle } from "react-icons/io5";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { IoCloseCircleOutline } from "react-icons/io5";
import WithAlert from "hoc/WithAlert";
import { UserBU, UserGroup, UserRoles } from "constants/UserConstants";
import { getBusinessUnit } from "utils/Common";

class AddUserForm extends Component {
  constructor() {
    super();
    this.state = {
      username: "",
      role: null,
      group: null,
      skills: [],
      channels: [],
      isLoading: true,
      isFormSubmissionInProgress: false,
      isUserValidationInProgress: false,
      isUserValid: false,
      showValidationError: false,
      showGreenTick: false,
      showGroupChangeWarning: false,
    };
    this.userCreationData = [];
    this.skillsData = [];
    this.skillsOptions = [];
    this.channelsOptions = [];
  }

  componentDidMount() {
    this.fetchChannels();
    this.fetchSkills();
    this.populateFormWithExistingUserDetails();
  }

  fetchSkills = () => {
    uepClient()
      .get("/skills")
      .then((res) => {
        this.skillsData = res.data;
        this.skillsOptions = this.skillsData.map((skill) => {
          return {
            value: skill.skillId,
            label: skill.name,
          };
        });
        this.setState({
          isLoading: false,
        });
      })
      .catch((err) => {
        console.error(err.message);
      });
  };

  fetchChannels = () => {
    uepClient()
      .get("/user/channels")
      .then((res) => {
        this.channelsOptions = res.data.map((channel) => {
          return {
            value: channel,
            label: this.capitalizeChannelLabel(channel),
          };
        });
        this.setState({
          isLoading: false,
        });
      })
      .catch((err) => {
        console.error(err.message);
      });
  };

  validateUser = (e) => {
    e.preventDefault();
    this.setState({ isUserValidationInProgress: true });
    uepClient()
      .post(`/user/validate/${this.state.username}`)
      .then(() => {
        this.setState({
          isUserValid: true,
          isLoading: false,
          isUserValidationInProgress: false,
          showValidationError: false,
          showGreenTick: true,
        });
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          //User not valid
          this.setState({ showValidationError: true, showGreenTick: false });
        } else {
          this.props.showAlert({
            variant: "danger",
            content: (
              <span>
                <b>Error!</b> Failed to validate user, Please try again.
              </span>
            ),
          });
        }
        console.error(err.message);
        this.setState({ isUserValidationInProgress: false });
      });
  };

  populateFormWithExistingUserDetails = () => {
    const { userToUpdate, isEditMode } = this.props;
    if (isEditMode && userToUpdate) {
      const selectedSkills = userToUpdate.skills.map((skill) => {
        return {
          value: skill.skillId,
          label: skill.name,
        };
      });

      const selectedChannels = userToUpdate.channels.map((channel) => {
        return {
          value: channel,
          label: this.capitalizeChannelLabel(channel),
        };
      });

      this.setState({
        username: userToUpdate.username,
        role: userToUpdate.role,
        group: userToUpdate.group,
        skills: selectedSkills,
        channels: selectedChannels,
      });
    }
  };

  capitalizeChannelLabel = (channel) => {
    return channel === "WHATSAPP"
      ? "WhatsApp"
      : channel.charAt(0).toUpperCase() + channel.toLowerCase().slice(1);
  };

  onFormSubmit = (e) => {
    e.preventDefault();
    const { isEditMode } = this.props;
    isEditMode ? this.submitUserEditForm() : this.submitUserCreationForm();
  };

  submitUserCreationForm = () => {
    const { username, role, group, skills, channels } = this.state;

    const { onFormClose, addNewUserToTable } = this.props;

    const userCreationData = {
      username: username,
      channels: this.getSelectedDropdownOptionValues(channels),
      skillIds: this.getSelectedDropdownOptionValues(skills),
      role: role,
      group: group,
    };
    this.setState({ isFormSubmissionInProgress: true });

    uepClient()
      .post("/user/add", JSON.stringify(userCreationData))
      .then((res) => {
        const userData = res.data;
        this.setState({ isFormSubmissionInProgress: false });
        this.props.showAlert({
          variant: "success",
          content: (
            <span>
              <b>Success!</b> The user has been created successfully.
            </span>
          ),
        });
        addNewUserToTable(userData);
      })
      .catch((err) => {
        let errorMessageToDisplay;
        switch (err.response.status) {
          case 409: //user already exists
            errorMessageToDisplay = err.response.data;
            break;
          case 400: //bu or group cannot be null for agent or supervisor
            errorMessageToDisplay = err.response.data;
            break;
          default:
            errorMessageToDisplay = "Failed to add user, Please try again.";
            break;
        }
        this.props.showAlert({
          variant: "danger",
          content: (
            <span>
              <b>Error!</b> {errorMessageToDisplay}
            </span>
          ),
        });
        console.error(err.message);
        this.setState({ isFormSubmissionInProgress: false });
      })
      .finally(() => {
        onFormClose();
      });
  };

  submitUserEditForm = () => {
    const { username, role, group, skills, channels } = this.state;

    const { onFormClose, updateUserInTable } = this.props;

    const updatedUserData = {
      username: username,
      channels: this.getSelectedDropdownOptionValues(channels),
      skillIds: this.getSelectedDropdownOptionValues(skills),
      role: role,
      group: group,
    };
    this.setState({ isFormSubmissionInProgress: true });

    uepClient()
      .post("/user/edit", JSON.stringify(updatedUserData))
      .then((res) => {
        const userData = res.data;
        this.setState({ isFormSubmissionInProgress: false });
        this.props.showAlert({
          variant: "success",
          content: (
            <span>
              <b>Success!</b> The user has been updated successfully.
            </span>
          ),
        });
        updateUserInTable(userData);
      })
      .catch((err) => {
        let errorMessageToDisplay;
        switch (err.response.status) {
          case 409: //user does not exist
            errorMessageToDisplay = err.response.data;
            break;
          case 400: //bu or group cannot be null for agent or supervisor
            errorMessageToDisplay = err.response.data;
            break;
          default:
            errorMessageToDisplay = "Failed to update user, Please try again.";
            break;
        }
        this.props.showAlert({
          variant: "danger",
          content: (
            <span>
              <b>Error!</b> {errorMessageToDisplay}
            </span>
          ),
        });
        console.error(err.message);
        this.setState({ isFormSubmissionInProgress: false });
      })
      .finally(() => {
        onFormClose();
      });
  };

  getRoleOptions = () => {
    return [
      {
        value: UserRoles.ADMIN,
        label: "Admin",
      },
      {
        value: UserRoles.SUPERVISOR,
        label: "Supervisor",
      },
      {
        value: UserRoles.AGENT,
        label: "Agent",
      },
    ];
  };

  getGroupOptions = () => {
    const businessUnit = getBusinessUnit();
    if (businessUnit === UserBU.PV) {
      return [
        {
          value: UserGroup.SALES,
          label: "Sales",
        },
      ];
    }
    return [
      {
        value: UserGroup.SALES,
        label: "Sales",
      },
      {
        value: UserGroup.SERVICE,
        label: "Service",
      },
      {
        value: UserGroup.OPPORTUNITY,
        label: "Opportunity",
      },
      {
        value: UserGroup.FLEET_EDGE,
        label: "Fleet Edge",
      }
    ];
  };

  onAgentIdInputChange = (e) => {
    this.setState({
      [e.target.name]: e.target.value,
      isUserValid: false,
      showGreenTick: false,
      showValidationError: false,
    });
  };

  renderFormInputWithHeader = (header, content, isBoldHeader = true) => {
    return (
      <FormInputWithHeader
        header={header}
        content={content}
        isBoldHeader={isBoldHeader}
      />
    );
  };

  onRoleChange = (role) => {
    this.setState({
      role,
      group: null,
    });
  };

  onGroupChange = (group) => {
    this.setState({
      group,
    });
    const { isEditMode } = this.props;
    if (isEditMode) {
      this.setState({
        showGroupChangeWarning: true,
      });
    }
  };

  getSkillsOptions = () => {
    return this.skillsOptions;
  };

  getChannelsOptions = () => {
    return this.channelsOptions;
  };

  onSkillChange = (selectedSkills) => {
    this.setState({
      skills: selectedSkills,
    });
  };

  onChannelChange = (selectedChannels) => {
    this.setState({
      channels: selectedChannels,
    });
  };

  getSelectedDropdownOptionValues = (selectedDropdownOptions) => {
    let values = [];
    if (selectedDropdownOptions && selectedDropdownOptions !== null) {
      values = selectedDropdownOptions.map((option) => {
        return option.value;
      });
    }
    return values;
  };

  isRadioButtonsEnabled = () => {
    return (
      this.state.role === UserRoles.AGENT ||
      this.state.role === UserRoles.SUPERVISOR
    );
  };

  getDropdownStyles = () => {
    const customStyles = {
      container: (provided) => ({
        ...provided,
        width: "100%",
      }),
      control: (provided, { isDisabled }) => ({
        ...provided,
        width: "100%",
        minWidth: 120,
        height: 34,
        minHeight: 34,
        font: "normal normal bold 14px/19px Roboto",
        background: "white",
        paddingLeft: "0px",
        opacity: isDisabled ? 0.5 : 1,
        boxShadow: "0px 3px 6px #00000029",
        border: 0,
      }),
      option: (provided) => ({
        ...provided,
        width: "100%",
        minWidth: 120,
        font: "Roboto",
        border: "none",
      }),
      dropdownIndicator: (provided, state) => ({
        ...provided,
        width: 28,
        paddingTop: 0,
        paddingBottom: 0,
        color: "#3c3c3f",
        transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : null,
      }),
      multiValue: (provided) => ({
        ...provided,
        fontFamily: "Roboto",
        color: "#96969B",
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        color: "#96969B",
        "&:hover": {
          backgroundColor: "#3A8AFA",
          color: "#fff",
        },
      }),
      menu: (provided) => ({
        ...provided,
      }),
      placeholder: (provided) => ({
        ...provided,
        fontWeight: "normal",
      }),
    };
    return customStyles;
  };

  canSubmit = () => {
    const { isEditMode } = this.props;
    const { username, role, isUserValid, group } = this.state;
    const isGroupSelected = role === UserRoles.ADMIN || Boolean(group);
    return isEditMode
      ? username && role
      : username && role && isUserValid && isGroupSelected;
  };

  onFormClose = () => {
    const { onFormClose } = this.props;
    onFormClose();
  };

  onFormCancel = (e) => {
    e.preventDefault();
    this.onFormClose();
  };

  isGroupSelectionEnabled = () => {
    return this.isRadioButtonsEnabled();
  };

  render() {
    const {
      username,
      role,
      group,
      skills,
      channels,
      isLoading,
      isFormSubmissionInProgress,
      isUserValidationInProgress,
      showValidationError,
      showGreenTick,
      showGroupChangeWarning,
    } = this.state;

    const { isEditMode } = this.props;

    return (
      <div className="add-user-form">
        <div className="add-user-form-header">
          <div className="user-icon-ctr">
            <IconWithBackground
              icon={<IoPersonCircle color="white" className="user-icon" />}
            />
          </div>
          {isEditMode ? (
            <div className="header-label">Edit User</div>
          ) : (
            <div className="header-label">Add User</div>
          )}
        </div>
        <div className="form-content">
          <form className="user-form" onSubmit={this.onFormSubmit}>
            <div className="form-section agent-id-and-validate">
              <Row>
                <Col xs="9">
                  {this.renderFormInputWithHeader(
                    "Agent Id",
                    <Input
                      className="agent-id-field"
                      name="username"
                      onChange={this.onAgentIdInputChange}
                      type="text"
                      pattern="^[a-zA-Z0-9_-]*$"
                      value={username}
                      disabled={isEditMode}
                    />
                  )}
                  {showGreenTick && (
                    <IoCheckmarkCircleOutline
                      size="1.5em"
                      color="#39C379"
                      className="green-tick-icon"
                    />
                  )}
                  {showValidationError && (
                    <IoCloseCircleOutline
                      size="1.5em"
                      color="#ff5a4e"
                      className="green-tick-icon"
                    />
                  )}
                  {showValidationError && (
                    <div className="validation-error">
                      Please enter valid ID
                    </div>
                  )}
                </Col>
                <Col xs="3">
                  <Button
                    className="user-validate-btn"
                    onClick={this.validateUser}
                    label="Validate"
                    isLoading={isUserValidationInProgress}
                    disabled={isEditMode}
                  />
                </Col>
              </Row>
            </div>
            <div className="form-section role">
              {this.renderFormInputWithHeader(
                "Role",
                <RadioGroup
                  options={this.getRoleOptions()}
                  onChange={this.onRoleChange}
                  value={role}
                />
              )}
            </div>
            <div className="form-section bu-and-group">
              <Row noGutters>
                <Col xs="6">
                  {this.renderFormInputWithHeader(
                    "Group",
                    <RadioGroup
                      options={this.getGroupOptions()}
                      onChange={this.onGroupChange}
                      value={group}
                      disabled={!this.isGroupSelectionEnabled()}
                    />
                  )}
                </Col>
                {showGroupChangeWarning && (
                  <div className="validation-error">
                    Warning! You are changing the group.{" "}
                    <span className="warning-second-line">
                      This may affect behaviour of the logged-in user.
                    </span>
                  </div>
                )}
              </Row>
            </div>
            <div className="form-section skill">
              {this.renderFormInputWithHeader(
                <span>Skills</span>,
                <Select
                  isMulti
                  onChange={this.onSkillChange}
                  value={skills}
                  options={this.getSkillsOptions()}
                  placeholder="Select Skills"
                  styles={this.getDropdownStyles()}
                  isLoading={isLoading}
                />,
                true
              )}
            </div>
            <div className="form-section channel">
              {this.renderFormInputWithHeader(
                <span>Channels </span>,
                <Select
                  isMulti
                  onChange={this.onChannelChange}
                  value={channels}
                  options={this.getChannelsOptions()}
                  placeholder="Select Channels"
                  styles={this.getDropdownStyles()}
                  isLoading={isLoading}
                />,
                true
              )}
            </div>
            <div className="buttons-container">
              <Button
                className="form-cancel-btn"
                label="Cancel"
                disabled={isFormSubmissionInProgress}
                onClick={this.onFormCancel}
                isTransparent
              />
              {isEditMode ? (
                <Button
                  className="form-submit-btn"
                  type="submit"
                  label="Update"
                  disabled={!this.canSubmit()}
                  isLoading={isFormSubmissionInProgress}
                />
              ) : (
                <Button
                  className="form-submit-btn"
                  type="submit"
                  label="Add"
                  disabled={!this.canSubmit()}
                  isLoading={isFormSubmissionInProgress}
                />
              )}
            </div>
          </form>
        </div>
      </div>
    );
  }
}

export default WithAlert(AddUserForm);
